import {copyObj} from "@/scripts/general";
import {API} from "@/api/generated";
import {BASEURL} from "../../http";
import {useQuizStore} from "@/stores/quizData";
import type {QuizPathValue} from "./modifyQuiz.types";

const INTERNAL_READDS_HOSTS = new Set(["ya.readds.ru", "reserve.readds.ru"]);

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const PUBLIC_READDS_HOSTS = new Set(["beta.readds.ru"]);

function getCurrentQuizHost(): string {
  const currentHost = window.location.host;
  const currentHostname = window.location.hostname;

  if (LOCAL_HOSTS.has(currentHostname)) {
    return currentHost;
  }

  if (PUBLIC_READDS_HOSTS.has(currentHostname)) {
    return currentHostname;
  }

  return "readds.ru";
}

function normalizePublicQuizBaseUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);

    if (INTERNAL_READDS_HOSTS.has(parsedUrl.hostname)) {
      parsedUrl.host = getCurrentQuizHost();
    }

    parsedUrl.pathname = "/quiz/";
    parsedUrl.search = "";
    parsedUrl.hash = "";

    return parsedUrl.toString();
  } catch {
    const currentHost = getCurrentQuizHost();
    const protocol = LOCAL_HOSTS.has(window.location.hostname)
      ? "http"
      : "https";

    return `${protocol}://${currentHost}/quiz/`;
  }
}

function isObjectWithProperties(
  value: unknown
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readProperty(value: unknown, key: string | number): unknown {
  return isObjectWithProperties(value) ? value[key] : undefined;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function getInputValue(value: unknown): unknown {
  const target = readProperty(value, "target");
  return (
    readProperty(target, "value") ?? readProperty(target, "innerHTML") ?? value
  );
}

export function modifyQuiz<const Path extends readonly (string | number)[]>(
  action: "get",
  numOrValue: unknown,
  keys: Path,
  notABtest?: boolean
): QuizPathValue<Path> | undefined;
export function modifyQuiz(
  action: "set" | "push" | "splice",
  numOrValue: unknown,
  keys: readonly (string | number)[],
  notABtest?: boolean
): void;
export function modifyQuiz(
  action: string,
  numOrValue: unknown,
  keys: readonly (string | number)[],
  notABtest = false
): unknown {
  const quizStore = useQuizStore();
  const originalQuiz = quizStore.getQuiz;
  const lk = document.location.pathname.includes("quiz/setting/");
  const isOriginalABTest =
    originalQuiz.nowReturn === "ABtest" && !lk && originalQuiz.ABTestBool;

  const isStoredABTest = localStorage.getItem("ABTest") === "true";
  const ABTest = (isOriginalABTest || isStoredABTest) && !notABtest;

  function isMobileVersion(val: boolean | string): boolean {
    return val === true || (val === "Мобильная" && !keys.includes("questions"));
  }

  const rawVersionText = quizStore.getSelectedVersion;
  const rawVersionBool = quizStore.getMobileVersion;
  const mobileVersion =
    isMobileVersion(rawVersionText) || isMobileVersion(rawVersionBool);

  const isTextContactButtonPath =
    keys.length === 1 && keys[0] === "textContactButton";

  const additionalKeys = [
    mobileVersion ? "mobileVersionQuiz" : "",
    ABTest ? "ABTestObj" : ""
  ].filter(Boolean);

  const fullPath = [...additionalKeys, ...keys];
  const lastKey = fullPath[fullPath.length - 1];
  const pathToObject = fullPath.slice(0, -1);

  numOrValue = getInputValue(numOrValue);

  if (action === "get") {
    if (isTextContactButtonPath) {
      return getTextContactButtonValue(originalQuiz, mobileVersion, ABTest);
    }

    let current: unknown = originalQuiz;
    for (const key of pathToObject) {
      if (!isObjectWithProperties(current) || !(key in current)) {
        if (!notABtest) {
          return modifyQuiz(action, numOrValue, keys, true);
        }
        return undefined;
      }
      current = current[key];
    }

    const value = readProperty(current, lastKey);
    if (value === undefined && !notABtest) {
      return modifyQuiz(action, numOrValue, keys, true);
    }

    if ((Array.isArray(value) || typeof value === "object") && !notABtest) {
      if (Array.isArray(value)) {
        const items: unknown[] = value;
        return items;
      }
      let orig = modifyQuiz(action, numOrValue, keys, true);
      return copyIsOrigInAb(orig, value);
    }

    return value;
  }

  const quiz = copyObj(originalQuiz);

  if (action === "set" && isTextContactButtonPath) {
    const textContactButtonTarget = getTextContactButtonTarget(
      quiz,
      mobileVersion,
      ABTest
    );

    if (textContactButtonTarget) {
      if (typeof numOrValue !== "string") {
        throw new TypeError("Contact button text must be a string");
      }
      const key = mobileVersion ? "mobile" : "pc";
      textContactButtonTarget[key] = numOrValue;
      quizStore.setQuiz(quiz);
      return;
    }
  }

  let current: Record<string, unknown> = quiz;
  if (Array.isArray(current["ABTestObj"])) {
    current["ABTestObj"] = {};
  }
  for (const key of pathToObject) {
    const child = current[key];
    if (isObjectWithProperties(child)) {
      current = child;
    } else {
      const childObject: Record<string, unknown> = {};
      current[key] = childObject;
      current = childObject;
    }
  }

  switch (action) {
    case "push": {
      const value = current[lastKey];

      if (isUnknownArray(value)) {
        value.push(numOrValue);
      } else {
        current[lastKey] = [numOrValue];
      }
      break;
    }
    case "splice": {
      if (
        (typeof numOrValue !== "number" && typeof numOrValue !== "string") ||
        !Number.isFinite(Number(numOrValue))
      ) {
        throw new TypeError("Array index must be a finite number");
      }
      const value = current[lastKey];
      if (isUnknownArray(value)) {
        const items = value.slice();
        items.splice(Number(numOrValue), 1);
        current[lastKey] = items;
      }
      break;
    }
    case "set":
      current[lastKey] = numOrValue;
      break;
  }

  quizStore.setQuiz(quiz);
  return undefined;
}

function isVersionedTextContactButton(
  value: unknown
): value is {pc?: string; mobile?: string} {
  return (
    isObjectWithProperties(value) &&
    !Array.isArray(value) &&
    (value.pc === undefined || typeof value.pc === "string") &&
    (value.mobile === undefined || typeof value.mobile === "string")
  );
}

function normalizeTextContactButton(
  value: unknown,
  mobileVersion: boolean
): string | undefined {
  if (typeof value === "string") {
    return value;
  }

  if (!isVersionedTextContactButton(value)) {
    return undefined;
  }

  const currentVersionValue = mobileVersion ? value.mobile : value.pc;
  const fallbackValue = mobileVersion ? value.pc : value.mobile;

  return currentVersionValue ?? fallbackValue;
}

function getTextContactButtonCandidates(
  quiz: unknown,
  mobileVersion: boolean,
  ABTest: boolean
): unknown[] {
  return [
    mobileVersion
      ? readProperty(
          readProperty(quiz, "mobileVersionQuiz"),
          "textContactButton"
        )
      : undefined,
    ABTest
      ? readProperty(readProperty(quiz, "ABTestObj"), "textContactButton")
      : undefined,
    readProperty(quiz, "textContactButton")
  ];
}

function getTextContactButtonValue(
  quiz: unknown,
  mobileVersion: boolean,
  ABTest: boolean
): string | undefined {
  const values = getTextContactButtonCandidates(quiz, mobileVersion, ABTest);

  const versionedTextContactButton = values.find(isVersionedTextContactButton);

  if (versionedTextContactButton) {
    return normalizeTextContactButton(
      versionedTextContactButton,
      mobileVersion
    );
  }

  for (const value of values) {
    const normalizedValue = normalizeTextContactButton(value, mobileVersion);

    if (normalizedValue !== undefined) {
      return normalizedValue;
    }
  }

  return undefined;
}

function getTextContactButtonTarget(
  quiz: unknown,
  mobileVersion: boolean,
  ABTest: boolean
): {pc?: string; mobile?: string} | undefined {
  const values = getTextContactButtonCandidates(quiz, mobileVersion, ABTest);

  return values.find(isVersionedTextContactButton);
}

function copyIsOrigInAb(orig: unknown, obj: unknown): unknown {
  if (!isObjectWithProperties(orig) || !isObjectWithProperties(obj)) {
    return obj;
  }
  for (const key in orig) {
    const isArrayIndex = !isNaN(Number(key));
    if (Object.prototype.hasOwnProperty.call(orig, key)) {
      let newVale = orig[key];
      if (typeof newVale === "object" || Array.isArray(newVale)) {
        if (!obj[key]) {
          obj[key] = newVale;
        }
        if (!isArrayIndex) {
          newVale = copyIsOrigInAb(orig[key], obj[key]);
        }
      }
      if (!isArrayIndex && !obj[key]) {
        obj[key] = newVale;
      }
    }
  }
  return obj;
}

function imgFromQuestion(file: File): boolean {
  if (file.name.includes("variant") || file.name.includes("information")) {
    return true;
  }
  return false;
}

export async function saveImgQuiz(
  file: File,
  mobileVersion: boolean
): Promise<string> {
  const quizStore = useQuizStore();
  function considerVersion(version: boolean): boolean {
    return version && !imgFromQuestion(file);
  }

  const ABTest = localStorage.getItem("ABTest") === "true";
  let formData = new FormData();
  const originalQuiz = quizStore.getQuiz;
  let nameSplit = file.name.split(".");
  let type = nameSplit[nameSplit.length - 1];
  let blob = file.slice(0, file.size, file.type);

  let newName = nameSplit.slice(0, nameSplit.length - 1).join(".");
  if (considerVersion(mobileVersion)) {
    newName += `Mobile`;
  }
  if (considerVersion(ABTest)) {
    newName += `ABTest`;
  }
  newName += `.${type}`;

  let newFile = new File([blob], newName, {type: file.type});

  formData.append("file", newFile);
  formData.append("id", originalQuiz["_id"]);
  formData.append(
    "mobileVersion",
    considerVersion(mobileVersion) ? "true" : "false"
  );
  formData.append("ABTest", considerVersion(ABTest) ? "true" : "false");

  const query = {
    mobileVersion: "false",
    ABTest: "false"
  };
  const headers = {"Content-Type": "multipart/form-data"};

  if (considerVersion(mobileVersion)) {
    query["mobileVersion"] = "true";
  }
  if (considerVersion(ABTest)) {
    query["ABTest"] = "true";
  }

  try {
    if (
      ["video/mp4", "image/svg+xml", "image/gif", "video/webm"].includes(
        newFile.type
      ) ||
      newName.includes("logo")
    ) {
      return await API.setSvg({
        body: formData,
        query,
        headers
      });
    }
    return await API.setRightPhoto({body: formData, headers});
  } catch {
    throw "Ошибка при загрузке картинки";
  }
}

export async function getBaseUrl(): Promise<string> {
  try {
    const domain = await API.getDomain();
    return normalizePublicQuizBaseUrl(domain);
  } catch {
    return normalizePublicQuizBaseUrl(BASEURL.replace("api", "quiz/"));
  }
}
